import { useContext, useState } from "react";
import { Box, Button, CircularProgress, Paper, TableContainer, Typography } from "@mui/material";
import { CheckCircleOutline, Close, Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

export const ResetPassword = ({ userId }) => {
  const [state, setState] = useState({ waiting: false, error: null, result: null });

  const { setModule } = useContext(ModuleContext);
  const { setUserModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  const sendPasswordReset = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/password`,
        {
          body: JSON.stringify({ userId, callerId: authState.accessToken.claims.sub }),
          signal: AbortSignal.timeout(360000),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: authState.accessToken.accessToken,
          },
        }
      );

      if (!res.ok) {
        if (res.status === 504) throw new Error("The request timed out. Please try again later.");
        const errorBody = await res.json().catch(() => ({}));
        throw new Error(errorBody?.error?.message || "An unknown error occurred.");
      }

      await res.json();

      setUserModule((prev) => ({
        ...prev,
        userData: { ...prev.userData, "Okta Status": "RECOVERY" },
      }));

      setState((prev) => ({ ...prev, waiting: false, result: "Password reset link sent" }));
    } catch (err) {
      if (err.name === "AbortError") {
        setState((prev) => ({ ...prev, waiting: false, error: new Error("Connection timed out. Please try again.") }));
      } else if (err.name === "TypeError") {
        setState((prev) => ({ ...prev, waiting: false, error: new Error("Network error. Please try again.") }));
      } else {
        setState((prev) => ({ ...prev, waiting: false, error: err }));
      }
    }
  };

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Password reset failed
          </Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      </TableContainer>
    );
  }

  if (state.result) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Password Reset Sent</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Confirmation
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">{state.result}</Typography>
        </Box>
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            endIcon={<Close />}
            onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
            sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
          >
            Close
          </Button>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Reset Password
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{userId}</Typography>
      </Box>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Send a password reset link to {userId}?
        </Typography>
      </Box>
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          endIcon={<Close />}
          onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
          sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={sendPasswordReset}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
          }}
        >
          Send Reset Link
        </Button>
      </Box>
    </TableContainer>
  );
};
